import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from 'react';

import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';

export type InputTypeT = 'email' | 'number' | 'password' | 'tel' | 'text' | 'date';

export type InputPropsT = {
  id: string;
  label: ReactNode;
  name: string;
  'aria-activedescendant'?: string;
  'aria-autocomplete'?: 'both' | 'inline' | 'list' | 'none';
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  autoComplete?: string;
  className?: string;
  defaultValue?: string | number;
  disabled?: boolean;
  errorMessage?: ReactNode;
  helperText?: ReactNode;
  inputMode?: 'decimal' | 'email' | 'numeric' | 'tel' | 'text';
  max?: string | number;
  maxLength?: number;
  min?: string | number;
  minLength?: number;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onPaste?: ClipboardEventHandler<HTMLInputElement>;
  optional?: boolean;
  placeholder?: string;
  prefix?: ReactNode;
  readOnly?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  role?: React.AriaRole;
  step?: number;
  success?: boolean;
  suffix?: ReactNode;
  type?: InputTypeT;
  value?: string | number;
};

export type InputT = React.FC<InputPropsT>;

export const Input: InputT = ({
  className,
  errorMessage,
  helperText,
  id,
  label,
  optional,
  prefix,
  success,
  suffix,
  ...inputProps
}) => {
  const control = (
    <Form.Control
      {...inputProps}
      aria-describedby={`${helperText ? `${id}-helper` : ''} ${errorMessage ? `${id}-error` : ''}`}
      aria-invalid={!!errorMessage}
      isInvalid={!!errorMessage}
      isValid={success}
      readOnly={inputProps.readOnly}
    />
  );

  return (
    <div className={className}>
      <Form.Group controlId={id} className="form-group">
        {label && (
          <Form.Label>
            {label}
            {optional && (
              <span className="optional-label"> (Optional)</span>
            )}
          </Form.Label>
        )}

        {helperText && <p id={`${id}-helper`} className="helper-text">{helperText}</p>}

        {(prefix || suffix)
          ? (
            <InputGroup>
              {prefix && <InputGroup.Text aria-hidden="true" className="prefix">{prefix}</InputGroup.Text>}

              {control}

              {suffix && <InputGroup.Text className="suffix">{suffix}</InputGroup.Text>}
            </InputGroup>
          )
          : control}

        {errorMessage && (
          <Form.Control.Feedback
            className={prefix || suffix ? 'd-block' : undefined}
            type="invalid"
            id={`${id}-error`}
          >
            {errorMessage}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    </div>
  );
};
